import { useState, useCallback, useEffect } from 'react';
import APIs from '../../../../../application/APIs';
import { PlaceholderStyle, Placeholder, PageSize, FriendlyColumn } from './types';

interface PDFInfoState {
  pageCount: number;
  pageSizes: PageSize[];
  currentPage: number;
  scale: number;
  loading: boolean;
  error: string | null;
}

interface LoadedData {
  placeholders: Placeholder[];
  dbColumns: string[];
  friendlyColumns: FriendlyColumn[];
}

interface UsePDFInfoProps {
  templateId: string;
  onDataLoaded: (data: LoadedData) => void;
}

export const usePDFInfo = ({ templateId, onDataLoaded }: UsePDFInfoProps) => {
  const [state, setState] = useState<PDFInfoState>({
    pageCount: 0,
    pageSizes: [],
    currentPage: 1,
    scale: 1.0,
    loading: true,
    error: null,
  });

  // Load PDF info, DB columns, and friendly columns on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setState(s => ({ ...s, loading: true, error: null }));

        const [pdfInfo, columnsResponse, friendlyColumnsResponse] = await Promise.all([
          APIs.getTemplatePDFInfo(templateId),
          APIs.getDBColumns(),
          APIs.getDBColumnsFriendly(),
        ]);

        const loadedPlaceholders = pdfInfo.placeholders.map((p: Placeholder) => ({
          ...p,
          style: p.style as PlaceholderStyle,
        }));

        setState(s => ({
          ...s,
          pageCount: pdfInfo.page_count,
          pageSizes: pdfInfo.page_sizes,
          loading: false,
        }));

        onDataLoaded({
          placeholders: loadedPlaceholders,
          dbColumns: columnsResponse.columns,
          friendlyColumns: friendlyColumnsResponse.columns,
        });
      } catch (error) {
        setState(s => ({
          ...s,
          loading: false,
          error: 'Failed to load PDF information',
        }));
      }
    };
    loadData();
  // onDataLoaded is stable (useCallback in orchestrator), safe to include
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  const setCurrentPage = useCallback((page: number) => {
    setState(s => ({ ...s, currentPage: page }));
  }, []);

  const setScale = useCallback((scale: number) => {
    setState(s => ({ ...s, scale }));
  }, []);

  return {
    pageCount: state.pageCount,
    pageSizes: state.pageSizes,
    currentPage: state.currentPage,
    scale: state.scale,
    loading: state.loading,
    error: state.error,
    setCurrentPage,
    setScale,
  };
};
